import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin } from 'rxjs';
import { AtokuCategory, AtokuCategoryUpsertRequest } from '../models/atoku-category.model';
import { AtokuService } from '../services/atoku.service';
import { EditCategoryDialogComponent } from './dialogs/edit-category-dialog/edit-category-dialog.component';

// const MOCK_CATEGORIES: AtokuCategory[] = [];

@Component({
    selector: 'app-atoku-categories',
    templateUrl: './atoku-categories.component.html',
    styleUrls: ['./atoku-categories.component.scss']
})
export class AtokuCategoriesComponent implements OnInit, AfterViewInit {
    displayedColumns: string[] = ['select', 'code', 'displayName', 'description', 'logo', 'theme'];
    dataSource = new MatTableDataSource<AtokuCategory>([]);
    selection = new SelectionModel<AtokuCategory>(true, []);
    private categories: AtokuCategory[] = [];

    @ViewChild(MatPaginator) paginator!: MatPaginator;

    constructor(
        private dialog: MatDialog,
        private atokuService: AtokuService
    ) { }

    ngOnInit(): void {
        this.loadCategories();
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
    }

    get hasSelection(): boolean {
        return this.selection.selected.length > 0;
    }

    get hasSingleSelection(): boolean {
        return this.selection.selected.length === 1;
    }

    applyFilter(event: Event): void {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    editSelectedCategory(): void {
        const category = this.selection.selected[0];
        if (!category) {
            return;
        }

        this.openEditDialog(category);
    }

    openAddDialog(): void {
        const dialogRef = this.dialog.open(EditCategoryDialogComponent, {
            width: '920px',
            maxWidth: '96vw',
            data: {
                mode: 'add',
                category: this.getEmptyCategory(),
                storageCategoryExists: this.categories.some(item => item.code?.toUpperCase() === 'STOR')
            }
        });

        dialogRef.afterClosed().subscribe((result?: AtokuCategoryUpsertRequest) => {
            if (!result) {
                return;
            }

            this.atokuService.createCategory(result).subscribe(category => {
                this.categories = [...this.categories, category];
                this.dataSource.data = [...this.categories];
            });
        });
    }

    openEditDialog(category: AtokuCategory): void {
        const dialogRef = this.dialog.open(EditCategoryDialogComponent, {
            width: '920px',
            maxWidth: '96vw',
            data: {
                mode: 'edit',
                category: { ...category }
            }
        });

        dialogRef.afterClosed().subscribe((result?: AtokuCategoryUpsertRequest) => {
            if (!result) {
                return;
            }

            this.atokuService.updateCategory(category.id, result).subscribe(updated => {
                this.categories = this.categories.map(item => item.id === category.id ? updated : item);
                this.dataSource.data = [...this.categories];
                this.selection.clear();
            });
        });
    }

    removeSelectedCategories(): void {
        const selectedIds = new Set(this.selection.selected.map(item => String(item.id)));
        if (!selectedIds.size) {
            return;
        }

        forkJoin(this.selection.selected.map(category => this.atokuService.deleteCategory(category.id)))
            .subscribe(() => this.loadCategories());
    }

    toggleRow(category: AtokuCategory): void {
        this.selection.toggle(category);
    }

    isAllSelected(): boolean {
        const rows = this.dataSource.filteredData;
        return rows.length > 0 && rows.every(row => this.selection.isSelected(row));
    }

    masterToggle(): void {
        const rows = this.dataSource.filteredData;
        if (!rows.length) {
            return;
        }

        if (this.isAllSelected()) {
            rows.forEach(row => this.selection.deselect(row));
            return;
        }

        rows.forEach(row => this.selection.select(row));
    }

    getLogoPreview(category: AtokuCategory): string {
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(category.logo)}`;
    }

    private loadCategories(): void {
        this.atokuService.getCategories().subscribe(categories => {
            this.categories = categories;
            this.dataSource.data = [...this.categories];
            this.selection.clear();
        });
    }

    private getEmptyCategory(): AtokuCategory {
        return {
            id: '',
            code: '',
            displayName: '',
            description: '',
            logo: '',
            theme: '',
            svgLibraryId: null,
            svgLibraryCode: null,
            svgLibraryName: null
        };
    }
}
